/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    func, object, string, bool, shape,
} from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import mbpLogger from 'mbp-logger';
import loadable from '@loadable/component';

import { useLDClient } from 'launchdarkly-react-client-sdk';
import mbpUtil from 'mbp-api-util';
import ConnectedCart from './Cart';
import MobileIconBlock from './MobileIconBlock';
import { country, getIsBot } from '../../../../state/ducks/App/App-Selectors';
import { LeftMenu } from '../../Header/MBPHeader';
import MobileSearchIconBlock from './MobileSearchIconBlock';
import { getCountryURL } from '../../../helpers/InternationalCountryURL';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

import noop from '../../../helpers/noop';
import ComponentErrorBoundary from '../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';
import useIsKeeper from '../../../helpers/hooks/useIsKeeper';

const MobileSignIn = loadable(() => import(/* webpackChunkName: "MobileSignIn" */ './MobileSignIn/MobileSignIn'));

let logoPlaced = false;

const headerLinkClassName = (classes, isGatheringsPage) => {
    // check if logo has been placed
    // use class names with CSS Grid properites to position before or after the logo
    if (logoPlaced) {
        // if it has, this link goes after the logo (link2 has grid-column: 4)
        logoPlaced = false;
        return `${classes.headerBlock} ${classes.link2} ${isGatheringsPage ? classes.gatheringsLink : ''}`;
    }
    // if it has not, this link goes before the logo (link1 has grid-column: 2)
    return `${classes.headerBlock} ${classes.link1}`;
};

const styles = (theme) => ({
    headerBlock: {
        // CSS Grid
        alignSelf: 'center',
        justifySelf: 'center',

        // Flexbox
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',

        textAlign: 'center',
        fontSize: '14px',
        letterSpacing: '0.6px',
        textTransform: 'lowercase',
        color: theme.palette.cms?.mobileMenuTextColor || theme.palette.mobileMenuColor,
        cursor: 'pointer',
        textDecoration: 'none',
        height: '42px',
        fontFamily: theme.typography?.fontFamily,

        '& img': {
            width: '26px',
            height: 'auto',
        },
    },
    menu: {
        // CSS Grid
        gridRow: '1',
        gridColumn: '1',

        // Button Overrides
        appearance: 'none',
        border: 'none',
        background: 'none',
        fontSize: '10px',
        fontFamily: 'Lato',
        textTransform: 'uppercase',
        letterSpacing: '.2px',
        borderRadius: '4px',
        '& img': {
            width: '24px',
            marginBottom: '3px',
        },
        '&:hover': {
            textDecoration: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
    },
    link1: {
        // CSS Grid
        gridRow: '1',
        gridColumn: '2',
    },
    flowersBrandLogo: {},
    logo: {
        // CSS Grid
        gridRow: '1',
        gridColumn: '3',

        width: 'auto',
        height: '100%',

        '& img': {
            width: 'auto',
            height: '100%',
            maxWidth: '174px',
            [theme.breakpoints.down(360)]: {
                maxWidth: '120px',
            },
            '&$flowersBrandLogo': {
                width: '111px',
                height: '54px',
            },
        },
    },
    flowersRadioIcon: {},
    flowersRadioText: {},
    link2: {
        // CSS Grid
        gridRow: '1',
        gridColumn: '4',
        '& img': {
            '&$flowersRadioIcon': {
                width: '25px',
                height: '25px',
                marginBottom: '2px',
            },
        },
        '& span': {
            '&$flowersRadioText': {
                fontSize: '10px',
                textTransform: 'uppercase',
                fontWeight: 'bold',
            },
        },
    },
    cart: {
        // CSS Grid
        gridRow: '1',
        gridColumn: '5',
        fontSize: '10px',
        fontFamily: theme.typography?.secondaryFontFamily,
        textTransform: 'uppercase',
        letterSpacing: '.2px',
        borderRadius: '50%',
        padding: '12px',
        height: '100%',
        '& img': {
            width: '24px',
        },
        '&:hover': {
            textDecoration: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
    },
    search: {
        gridRow: '1',
        gridColumn: '4',
        fontSize: '10px',
        fontFamily: theme.typography?.secondaryFontFamily,
        textTransform: 'uppercase',
        letterSpacing: '.2px',
        borderRadius: '50%',
        border: 'none',
        background: 'none',
        outline: 'none',
        padding: '12px',
        height: '100%',
        '& img': {
            width: '24px',
        },
        '&:hover': {
            textDecoration: 'none',
        },
        '& svg': {
            fontSize: '24px',
        },
    },
    gatheringBrandLogo: {
        height: '100%',
        display: 'flex',
        gridRow: '1',
        alignSelf: 'center',
        gridColumn: '4',
        flexDirection: 'column',
        justifyContent: 'space-around',
        '& img': {
            width: '100%',
            aspectRatio: '5/1',
            borderLeft: '1px solid',
            padding: '0px 10px',
            marginLeft: 5,
            [theme.breakpoints.up(800)]: {
                height: '35px',

            },
        },
    },
    gatheringsLink: {
        gridColumn: 5,
    },
    flowerLogoMain: {
        justifyContent: 'flex-start',
        '& img': {
            '&$flowersBrandLogo': {
                height: '45px',
            },
        },
    },
});

const MobileHeaderBlock = ({
    block, brand, classes, clickHandler, showSearch, isGatheringsPage, mobileMenuCampaignResponse,
    presentationFamily, logo, keeperData, isSearchUiInMobileMenuEnabled,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const isSimplifiedMobileNavEnabled = useSelector(getFeatureFlag('is-simplified-mobile-nav-enabled'));
    const isConvUiHeaderEnabled = useSelector(getFeatureFlag('is-conv-ui-header-enabled'));
    const isBot = useSelector(getIsBot);
    const isKeeper = useIsKeeper();
    const blockKey = Object.keys(block)[0];
    const blockObj = block[blockKey];
    const countryName = useSelector(country);
    const countryURL = getCountryURL(countryName, history?.location?.pathname);
    const inCheckout = history?.location?.pathname?.indexOf('/checkout/') >= 0;
    const countryIsCanadaOrEngland = blockObj?.link?.href === '/canada' || blockObj?.link?.href === '/england';
    const triggerChat = () => {
        const iframe = document?.querySelector(`iframe[src="${mbpUtil.getEnv('APP_CONVERSATIONAL_UI_HOST')}/chat/flora"]`);
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow?.postMessage(
                { type: 'chat-header-button', data: { clicked: true } },
                mbpUtil.getEnv('APP_CONVERSATIONAL_UI_HOST'),
            );
        }
    };
    // AB TEST isSimplifiedMobileNavEnabled
    let ldVariant;
    // CSR only
    if (typeof window !== 'undefined') {
        const ldClient = useLDClient();
        ldVariant = ldClient?.variation('which-simplified-mobile-nav')?.variant;
        if (isSimplifiedMobileNavEnabled && ldVariant) {
            dispatch(trackEvent({
                eventCategory: 'Experiment',
                eventAction: 'Mobile Nav',
                eventLabel: ldVariant,
                nonInteraction: true,
                queued: true,
            }));
        }
    }

    if (!blockObj) return null;

    let output = null;

    switch (blockKey) {
        case 'menu': {
            // server side to prevent swapping or flickering
            if (isSimplifiedMobileNavEnabled && !ldVariant && !isBot) {
                break;
            }
            // client side
            // ab test if enabled and has variant value enter flow
            if (isSimplifiedMobileNavEnabled && ldVariant && !isBot) {
                // loop over the menu_ref array
                blockObj.menu_ref.forEach((menuRefItem, i) => {
                    // if the current title includes the variant value
                    // example:
                    // title is: Top Nav Menu Mobile - VARIANT A 1.19.23
                    // ldVariant: could be (CONTROL or VARIANT A)
                    if (menuRefItem.title.includes(ldVariant)) {
                        output = (
                            <LeftMenu
                                menu={blockObj.menu_ref?.[i]?.menu_blocks || []}
                                contentType={blockObj.menu_ref?.[i]?._content_type_uid}
                                uid={blockObj.menu_ref?.[i]?.uid}
                                brand={brand}
                                logo={logo?.logos || {}}
                                guidedNav={blockObj?.guided_nav || []}
                                menuIcon={{
                                    name: blockObj.name,
                                    url: blockObj.menu_icon?.url,
                                    description: blockObj.name,
                                    className: `${classes.headerBlock} ${classes.menu}`,
                                }}
                                keeperData={keeperData}
                            />
                        );
                    }
                });
                break;
            }
            output = (
                <LeftMenu
                    menu={blockObj.menu_ref?.[0]?.menu_blocks || []}
                    contentType={blockObj.menu_ref?.[0]?._content_type_uid}
                    uid={blockObj.menu_ref?.[0]?.uid}
                    brand={brand}
                    logo={logo?.logos || {}}
                    guidedNav={blockObj?.guided_nav || []}
                    menuIcon={{
                        name: blockObj.name,
                        url: blockObj.menu_icon?.url,
                        description: blockObj.name,
                        className: `${classes.headerBlock} ${classes.menu}`,
                    }}
                    keeperData={keeperData}
                    isSearchUiInMobileMenuEnabled={isSearchUiInMobileMenuEnabled}
                    mobileMenuCampaignResponse={mobileMenuCampaignResponse}
                />
            );
            break;
        }
        case 'header_links':
            output = (

                <MobileIconBlock
                    name={blockObj.link?.title}
                    linkHref={blockObj.link?.href}
                    description={blockObj.link?.title}
                    imgSrc={blockObj.icon?.url}
                    className={headerLinkClassName(classes, isGatheringsPage)}
                    classes={classes}
                    brandCode={brand.code}
                />

            );
            break;

        case 'icon_button':
            if ((blockObj.type === 'GUEST' || blockObj.type === 'REGISTERED') && !inCheckout) {
                output = (
                    <ComponentErrorBoundary module="MobileSignIn" function="MobileHeaderBlock">
                        <MobileSignIn
                            title={blockObj?.title}
                            imageUrl={blockObj?.icon?.url}
                            presentationFamily={presentationFamily}
                            type={blockObj.type}
                        />
                    </ComponentErrorBoundary>
                );
            }
            break;

        case 'logos':
            logoPlaced = true;
            output = (
                <>
                    <Link to={countryIsCanadaOrEngland ? blockObj?.link?.href : countryURL} alt={blockObj.image?.title} aria-label="Mobile-Logo" className={`${classes.headerBlock} ${classes.logo} ${isGatheringsPage ? classes.flowerLogoMain : ''}`}>
                        <img src={blockObj.image?.url} className={`${presentationFamily === 'flower' && classes.flowersBrandLogo}`} alt={blockObj.image?.title} width="174" height="54" />
                    </Link>
                    {isGatheringsPage && !isKeeper && (
                        <div className={classes.gatheringBrandLogo}>
                            <img src="https://images.contentstack.io/v3/assets/bltdd99f24e8a94d536/blted7c97fec58e7f30/610ba16e93c9a33d7352f57f/mobileGatheringUslogo.png" className={classes.flowersBrandLogo} alt="Gathering Logo" width="174" height="54" />
                        </div>
                    )}
                    {isKeeper && keeperData?.logo_mobile?.url && (
                        <div className={classes.gatheringBrandLogo}>
                            <img src={keeperData?.logo_mobile?.url} className={classes.flowersBrandLogo} alt="Keeper Logo" />
                        </div>
                    )}
                    {isConvUiHeaderEnabled && (
                        <button
                            type="button"
                            style={{
                                display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', background: 'none',
                            }}
                            onClick={() => { triggerChat(); }}
                        >
                            <svg width="35" height="35" viewBox="0 0 483 483" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_783_28989)">
                                    <mask id="mask0_783_28989" maskUnits="userSpaceOnUse" x="0" y="0" width="483" height="483">
                                        <path d="M483 0H0V483H483V0Z" fill="white" />
                                    </mask>
                                    <g mask="url(#mask0_783_28989)">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M166.106 162.774C149.506 131.152 164.016 90.028 184.834 64.4191H184.854C176.988 50.4417 164.351 36.0109 148.008 34C154.198 54.148 142.843 73.7243 132.651 89.9491C123.326 103.276 123.543 120.96 130.029 135.647C137.165 149.802 150.551 162.517 166.106 162.774ZM189.427 163.169C204.154 153.213 210.324 137.915 213.104 121.473V121.493C215.333 111.114 213.816 100.689 212.309 90.3359C210.284 76.4224 208.278 62.6401 215.411 49.2793C198.713 60.8319 184.44 74.5333 177.244 92.0002C166.657 114.869 163.7 144.066 176.554 166.146C181.305 166.579 185.642 165.199 189.427 163.169ZM126.657 151.811C136.317 165.413 142.783 180.574 139.393 198.041C138.151 202.161 136.061 206.202 132.394 209.238C107.692 202.713 89.1409 179.982 80.4469 156.305C73.1921 138.858 73.5863 119.085 77.2335 99.1143C81.6307 113.606 92.7948 121.934 104.066 130.341C112.451 136.596 120.896 142.896 126.657 151.811ZM122.616 214.246C88.5101 203.62 69.7027 164.27 66.3118 131.446L66.3315 131.426C50.8755 127.109 31.733 125.847 18.761 135.98C37.391 145.837 43.2067 167.72 47.465 186.409C50.3038 202.417 62.9407 214.778 77.9235 220.574C92.9852 225.542 111.438 225.049 122.616 214.246ZM86.972 234.395C103.414 237.175 118.712 243.346 128.668 258.072C130.698 261.857 132.078 266.195 131.645 270.946C109.565 283.799 80.3677 280.842 57.4992 270.256C40.0323 263.06 26.3309 248.787 14.7783 232.089C28.1391 239.221 41.9212 237.216 55.8347 235.191C66.1875 233.684 76.6129 232.167 86.9918 234.395H86.972ZM128.274 281.395C96.652 297.994 55.528 283.484 29.9191 262.666V262.646C15.9417 270.512 1.51086 283.149 -0.5 299.492C19.648 293.302 39.2243 304.658 55.4491 314.85C68.776 324.175 86.4597 323.958 101.147 317.472C115.302 310.335 128.017 296.949 128.274 281.395ZM117.313 320.84C130.915 311.18 146.076 304.714 163.543 308.104C167.663 309.346 171.704 311.436 174.74 315.103C168.215 339.805 145.484 358.356 121.808 367.05C104.36 374.305 84.5869 373.911 64.6164 370.264C79.1081 365.866 87.4358 354.702 95.8432 343.431C102.098 335.046 108.398 326.601 117.313 320.84ZM179.748 324.886C169.122 358.991 129.772 377.799 96.9481 381.19L96.9284 381.17C92.611 396.626 91.3492 415.769 101.482 428.741C111.34 410.111 133.222 404.295 151.912 400.037C167.92 397.198 180.28 384.561 186.076 369.578C191.044 354.516 190.552 336.064 179.748 324.886ZM199.896 360.531C202.676 344.089 208.846 328.791 223.573 318.835C227.358 316.805 231.695 315.424 236.446 315.858C249.3 337.938 246.343 367.135 235.756 390.004C228.56 407.47 214.287 421.172 197.589 432.724C204.722 419.364 202.716 405.582 200.691 391.668C199.184 381.315 197.667 370.89 199.896 360.511V360.531ZM246.894 319.226C263.494 350.847 248.984 391.971 228.166 417.58H228.146C236.012 431.558 248.649 445.988 264.992 447.999C258.802 427.851 270.157 408.275 280.349 392.05C289.674 378.723 289.457 361.04 282.971 346.352C275.835 332.198 262.449 319.482 246.894 319.226ZM286.343 330.187C276.683 316.585 270.216 301.424 273.607 283.957C274.849 279.837 276.939 275.796 280.606 272.76C305.308 279.285 323.859 302.016 332.553 325.693C339.808 343.14 339.414 362.913 335.766 382.884C331.369 368.392 320.205 360.064 308.934 351.657C300.549 345.402 292.104 339.102 286.343 330.187ZM290.385 267.752C324.49 278.378 343.298 317.728 346.689 350.552L346.669 350.572C362.125 354.889 381.267 356.151 394.239 346.018C375.609 336.161 369.794 314.278 365.535 295.589C362.697 279.581 350.06 267.22 335.077 261.424C320.015 256.456 301.563 256.949 290.385 267.752ZM326.028 247.606C309.586 244.827 294.288 238.656 284.332 223.929C282.301 220.144 280.921 215.807 281.355 211.056C303.435 198.202 332.632 201.159 355.501 211.746C372.967 218.942 386.669 233.215 398.221 249.913C384.861 242.78 371.079 244.786 357.165 246.811C346.812 248.318 336.387 249.835 326.008 247.606H326.028ZM284.726 200.607C316.348 184.007 357.472 198.517 383.08 219.335V219.355C397.058 211.489 411.489 198.852 413.5 182.509C393.352 188.699 373.775 177.344 357.55 167.152C344.224 157.827 326.54 158.044 311.853 164.53C297.698 171.666 284.982 185.052 284.726 200.607ZM295.687 161.157C282.085 170.817 266.924 177.284 249.457 173.893C245.337 172.651 241.296 170.561 238.26 166.894C244.785 142.192 267.516 123.641 291.193 114.947C308.64 107.692 328.413 108.087 348.384 111.734C333.892 116.131 325.564 127.295 317.157 138.566C310.902 146.952 304.602 155.396 295.687 161.157ZM233.253 157.115C243.879 123.009 283.228 104.202 316.053 100.811L316.072 100.831C320.39 85.3748 321.651 66.2323 311.518 53.2603C301.661 71.8903 279.778 77.706 261.089 81.9643C245.081 84.8031 232.72 97.44 226.924 112.423C221.956 127.485 222.449 145.937 233.253 157.115Z" fill="url(#paint0_linear_783_28989)" />
                                        <path d="M480.802 102.032C483.068 99.7665 483.068 96.0931 480.802 93.8274L480.801 93.8261C480.424 93.4493 479.997 93.1263 479.532 92.8659L466.794 85.7361C461.706 82.8882 457.507 78.6864 454.661 73.5969L447.562 60.8965C447.301 60.4305 446.977 60.0026 446.6 59.6251C444.334 57.3595 440.661 57.3593 438.395 59.6249C438.018 60.0025 437.694 60.4303 437.433 60.8963L430.334 73.5969C427.488 78.6865 423.289 82.8883 418.201 85.7362L405.463 92.8659C404.998 93.1263 404.571 93.4493 404.194 93.8262L404.193 93.8274C401.927 96.0931 401.927 99.7664 404.193 102.032C404.57 102.41 404.998 102.733 405.464 102.994L418.165 110.094C423.254 112.939 427.456 117.138 430.304 122.226L437.434 134.965C437.694 135.43 438.017 135.857 438.394 136.233L438.395 136.235C440.661 138.5 444.334 138.5 446.6 136.235L446.601 136.233C446.978 135.857 447.301 135.43 447.561 134.965L454.691 122.226C457.539 117.138 461.741 112.939 466.83 110.094L479.531 102.994C479.997 102.733 480.425 102.41 480.802 102.032Z" fill="url(#paint1_linear_783_28989)" />
                                        <path d="M401.624 118.231C401.429 118.036 401.208 117.869 400.968 117.734L394.374 114.043C391.74 112.569 389.566 110.394 388.093 107.76L384.418 101.185C384.283 100.944 384.116 100.723 383.92 100.527C382.747 99.3543 380.846 99.3543 379.673 100.527C379.478 100.723 379.31 100.944 379.175 101.185L375.5 107.76C374.027 110.394 371.853 112.569 369.22 114.043L362.626 117.734C362.385 117.869 362.164 118.036 361.969 118.231C360.796 119.404 360.796 121.306 361.968 122.479C362.164 122.674 362.385 122.842 362.627 122.977L369.201 126.652C371.835 128.125 374.011 130.299 375.485 132.932L379.176 139.526C379.31 139.767 379.477 139.988 379.673 140.183C380.845 141.356 382.747 141.356 383.92 140.184C384.115 139.989 384.283 139.767 384.418 139.526L388.109 132.932C389.583 130.299 391.758 128.125 394.393 126.652L400.967 122.977C401.208 122.842 401.43 122.674 401.625 122.479C402.798 121.306 402.797 119.404 401.624 118.231Z" fill="url(#paint2_linear_783_28989)" />
                                        <path d="M397.88 62.2898C397.762 62.1716 397.628 62.0703 397.482 61.9886L393.487 59.7525C391.891 58.8593 390.574 57.5415 389.682 55.9453L387.455 51.962C387.373 51.8159 387.272 51.6817 387.153 51.5633C386.443 50.8527 385.291 50.8526 384.58 51.5632C384.462 51.6816 384.36 51.8158 384.278 51.962L382.052 55.9453C381.159 57.5415 379.842 58.8593 378.246 59.7525L374.251 61.9886C374.106 62.0703 373.972 62.1716 373.854 62.2898C373.143 63.0004 373.143 64.1528 373.853 64.8634C373.972 64.9818 374.106 65.0833 374.252 65.165L378.235 67.3917C379.831 68.284 381.149 69.6011 382.042 71.1969L384.279 75.192C384.36 75.3378 384.462 75.4717 384.58 75.5899C385.29 76.3006 386.443 76.3009 387.153 75.5903C387.271 75.4721 387.373 75.3378 387.455 75.192L389.691 71.1969C390.584 69.6011 391.902 68.284 393.498 67.3917L397.481 65.165C397.628 65.0833 397.762 64.9818 397.88 64.8634C398.591 64.1529 398.59 63.0004 397.88 62.2898Z" fill="url(#paint3_linear_783_28989)" />
                                    </g>
                                </g>
                                <defs>
                                    <linearGradient id="paint0_linear_783_28989" x1="137.5" y1="436.499" x2="373.249" y2="62.7498" gradientUnits="userSpaceOnUse">
                                        <stop offset="0.0305501" stop-color="#10D0FA" />
                                        <stop offset="0.432018" stop-color="#4662F5" />
                                        <stop offset="0.569433" stop-color="#724EFE" />
                                        <stop offset="0.830373" stop-color="#E365FE" />
                                    </linearGradient>
                                    <linearGradient id="paint1_linear_783_28989" x1="342.304" y1="236.965" x2="465.403" y2="46.6447" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#89F0D0" />
                                        <stop offset="0.518191" stop-color="#724EFE" />
                                        <stop offset="0.830373" stop-color="#E365FE" />
                                    </linearGradient>
                                    <linearGradient id="paint2_linear_783_28989" x1="329.932" y1="192.326" x2="393.654" y2="93.8079" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#89F0D0" />
                                        <stop offset="0.518191" stop-color="#724EFE" />
                                        <stop offset="0.830373" stop-color="#E365FE" />
                                    </linearGradient>
                                    <linearGradient id="paint3_linear_783_28989" x1="354.443" y1="107.183" x2="393.051" y2="47.4922" gradientUnits="userSpaceOnUse">
                                        <stop stop-color="#89F0D0" />
                                        <stop offset="0.518191" stop-color="#724EFE" />
                                        <stop offset="0.830373" stop-color="#E365FE" />
                                    </linearGradient>
                                    <clipPath id="clip0_783_28989">
                                        <rect width="483" height="483" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </button>
                    )}
                </>

            );
            break;

        case 'widgets':
            if (blockObj.name === 'Shopping Cart') {
                output = (

                    <ConnectedCart
                        image={blockObj.icon && blockObj.icon?.url ? blockObj.icon?.url : null}
                        name={blockObj.link?.title}
                        description={blockObj.link?.title}
                        type="mobile"
                        isGatheringsPage={isGatheringsPage}
                    />

                );
            } else if (blockObj?.type === '[SEARCH]' && showSearch) {
                output = (

                    <MobileSearchIconBlock
                        name={blockObj.link?.title}
                        onSearchClick={clickHandler}
                        className={`${classes.headerBlock} ${classes.search}`}
                    />

                );
            } else {
                output = (
                    <MobileIconBlock
                        name={blockObj.link?.title}
                        linkHref={blockObj.link?.href}
                        description={blockObj.link?.title}
                        imgSrc={blockObj.icon && blockObj.icon?.url ? blockObj.icon?.url : null}
                        className={`${classes.headerBlock} ${classes.cart}`}
                    />
                );
            }
            break;

        default:
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                message: `Unknown header block type '${blockKey}' in MobileHeaderBlock`,
                brand,
                block: blockObj,
            });
            output = null;
            break;
    }

    return output;
};

MobileHeaderBlock.propTypes = {
    block: object.isRequired,
    classes: object.isRequired,
    brand: object.isRequired,
    clickHandler: func,
    showSearch: bool,
    isGatheringsPage: bool,
    presentationFamily: string.isRequired,
    logo: shape({
        logos: shape({
            image: shape({
                url: string,
            }),
        }),
    }),
    keeperData: object,
    isSearchUiInMobileMenuEnabled: bool,
    mobileMenuCampaignResponse: object,
};

MobileHeaderBlock.defaultProps = {
    clickHandler: noop,
    showSearch: false,
    isGatheringsPage: false,
    logo: {},
    keeperData: {},
    isSearchUiInMobileMenuEnabled: false,
    mobileMenuCampaignResponse: {},
};

export default withStyles(styles)(MobileHeaderBlock);
